/**
 * BoqTemplatePreview Component
 * Detailed line-by-line preview of a BOQ starter template.
 * Displays sections, line items, specifications, units, quantities, rates, and line totals
 * alongside assumptions and warnings.
 */

import { AlertCircle, ChevronDown, ChevronRight, Layers } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPricePaisa } from "@/lib/format";
import type {
	BOQStarterPreview,
	BOQStarterPreviewSection,
} from "../../services/boq/boq-api.service";
import { formatGstIncluded, summariseGstIncluded } from "../../utils/boq-gst";
import { warningText } from "./unpriced-copy";

interface BoqTemplatePreviewProps {
	preview: BOQStarterPreview;
}

export function BoqTemplatePreview({ preview }: BoqTemplatePreviewProps) {
	const { t, i18n } = useTranslation();
	// All sections expanded by default so user sees full breakdown
	const [expandedSections, setExpandedSections] = useState<
		Record<string, boolean>
	>(() => {
		const initial: Record<string, boolean> = {};
		for (const section of preview.sections) {
			initial[section.templateSectionId] = true;
		}
		return initial;
	});

	const toggleSection = (sectionId: string) => {
		setExpandedSections((prev) => ({
			...prev,
			[sectionId]: !prev[sectionId],
		}));
	};

	const totalItems = preview.sections.reduce(
		(count, section) => count + section.items.length,
		0,
	);

	return (
		<div
			className="space-y-4 rounded-xl p-5"
			style={{
				background: "var(--color-dashboard-surface)",
				border: "1px solid var(--color-dashboard-border)",
			}}
		>
			{/* ── Summary Header ─────────────────────────────────────────── */}
			<div
				className="flex flex-wrap items-center justify-between gap-4 border-b pb-4"
				style={{ borderColor: "var(--color-dashboard-border-soft)" }}
			>
				<div>
					<h4
						className="font-medium text-base"
						style={{
							fontFamily: "var(--font-display)",
							color: "var(--color-dashboard-text)",
						}}
					>
						{t("boq.starter.summaryTitle")}
					</h4>
					<p
						className="text-xs"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("boq.starter.summaryCounts", {
							sections: preview.sections.length,
							items: totalItems,
						})}
					</p>
				</div>
				<div className="text-right">
					<div
						className="text-xs font-mono uppercase tracking-wider"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("boq.starter.estimatedTotal")}
					</div>
					<div
						className="text-xl font-semibold"
						style={{
							fontFamily: "var(--font-display)",
							color: "var(--color-dashboard-accent-dark)",
						}}
					>
						{new Intl.NumberFormat(i18n.language, {
							style: "currency",
							currency: "INR",
						}).format(preview.estimatedTotal / 100)}
					</div>
					<div
						data-testid="starter-preview-gst"
						className="text-xs"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("boq.starter.gstIncluded")}:{" "}
						{formatGstIncluded(
							summariseGstIncluded(
								preview.sections.flatMap((section) => section.items),
							),
							"INR",
							(linesWithGst, lines) =>
								t("boq.starter.gstCoverage", {
									with: linesWithGst,
									total: lines,
								}),
						)}
					</div>
				</div>
			</div>

			{/* ── Assumptions Callout ─────────────────────────────────────── */}
			{preview.assumptions && preview.assumptions.length > 0 && (
				<div
					className="rounded-lg p-3 text-xs"
					style={{
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border-soft)",
					}}
				>
					<div
						className="mb-1.5 font-medium"
						style={{ color: "var(--color-dashboard-text)" }}
					>
						{t("boq.starter.assumptionsTitle")}
					</div>
					<ul
						className="space-y-1 list-disc pl-4"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{preview.assumptions.map((assumption) => (
							<li key={`${assumption.label}-${assumption.source}`}>
								<span className="font-medium text-dashboard-text">
									{assumption.label}:
								</span>{" "}
								{assumption.value}
							</li>
						))}
					</ul>
				</div>
			)}

			{/* ── Warnings Callout ────────────────────────────────────────── */}
			{preview.warnings && preview.warnings.length > 0 && (
				<div
					className="rounded-md border p-3 text-xs"
					style={{
						borderColor: "var(--color-dashboard-border)",
						background: "var(--color-dashboard-warm-100)",
						color: "var(--color-dashboard-accent-dark)",
					}}
				>
					<div className="font-medium flex items-center gap-1.5">
						<AlertCircle size={14} />
						{t("boq.starter.warningsTitle")}
					</div>
					<ul className="mt-1 list-disc space-y-1 pl-5">
						{preview.warnings.map((warning, index) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: order is stable
							<li key={`${warning.code}-${index}`}>
								{warningText(t, i18n.language, warning)}
							</li>
						))}
					</ul>
				</div>
			)}

			{/* ── Line-by-Line Section & Item Breakdown ──────────────────── */}
			<div className="space-y-3 pt-2">
				<div className="flex items-center justify-between text-xs font-medium uppercase tracking-wider text-dashboard-muted">
					<span className="flex items-center gap-1.5">
						<Layers size={13} /> Detailed Scope & Line Items Breakdown
					</span>
					<span>{preview.sections.length} Rooms / Sections</span>
				</div>

				{preview.sections.map((section: BOQStarterPreviewSection) => {
					const isExpanded =
						expandedSections[section.templateSectionId] ?? true;
					return (
						<div
							key={section.templateSectionId}
							className="rounded-lg overflow-hidden border"
							style={{
								borderColor: "var(--color-dashboard-border)",
								background: "var(--color-dashboard-paper)",
							}}
						>
							{/* Section Header */}
							<button
								type="button"
								onClick={() => toggleSection(section.templateSectionId)}
								className="w-full flex items-center justify-between px-3.5 py-2.5 text-left transition-colors hover:bg-dashboard-surface"
								style={{
									borderBottom: isExpanded
										? "1px solid var(--color-dashboard-border-soft)"
										: "none",
								}}
							>
								<div className="flex items-center gap-2">
									{isExpanded ? (
										<ChevronDown size={14} className="text-dashboard-muted" />
									) : (
										<ChevronRight size={14} className="text-dashboard-muted" />
									)}
									<span className="font-medium text-sm text-dashboard-text">
										{section.sectionName}
									</span>
									<span className="text-xs text-dashboard-muted">
										({section.items.length}{" "}
										{section.items.length === 1 ? "item" : "items"})
									</span>
								</div>
								<span className="text-sm font-semibold text-dashboard-accent-dark font-mono">
									{formatPricePaisa(section.sectionTotal)}
								</span>
							</button>

							{/* Items Table */}
							{isExpanded && (
								<div className="overflow-x-auto">
									<table className="w-full text-xs">
										<thead>
											<tr
												style={{
													background: "var(--color-dashboard-surface)",
													borderBottom:
														"1px solid var(--color-dashboard-border-soft)",
												}}
											>
												<th className="py-2 px-3 text-left font-mono uppercase text-dashboard-placeholder w-10">
													#
												</th>
												<th className="py-2 px-3 text-left font-mono uppercase text-dashboard-placeholder">
													Item / Specification
												</th>
												<th className="py-2 px-3 text-right font-mono uppercase text-dashboard-placeholder w-20">
													Unit
												</th>
												<th className="py-2 px-3 text-right font-mono uppercase text-dashboard-placeholder w-20">
													Qty
												</th>
												<th className="py-2 px-3 text-right font-mono uppercase text-dashboard-placeholder w-28">
													Rate (₹)
												</th>
												<th className="py-2 px-3 text-right font-mono uppercase text-dashboard-placeholder w-28">
													Amount (₹)
												</th>
											</tr>
										</thead>
										<tbody>
											{section.items.map((item, idx) => (
												<tr
													key={item.templateItemId || idx}
													style={{
														borderBottom:
															idx < section.items.length - 1
																? "1px solid var(--color-dashboard-surface)"
																: "none",
													}}
													className="hover:bg-dashboard-surface/40 transition-colors"
												>
													<td className="py-2 px-3 font-mono text-dashboard-placeholder">
														{idx + 1}
													</td>
													<td className="py-2 px-3">
														<div className="font-medium text-dashboard-text">
															{item.description}
														</div>
														{item.assumptions &&
															item.assumptions.length > 0 && (
																<div className="text-[10px] text-dashboard-muted mt-0.5">
																	{item.assumptions.join(" · ")}
																</div>
															)}
													</td>
													<td className="py-2 px-3 text-right font-mono text-dashboard-muted">
														{item.uom || "—"}
													</td>
													<td className="py-2 px-3 text-right font-mono text-dashboard-text font-medium">
														{item.quantity}
													</td>
													<td className="py-2 px-3 text-right font-mono text-dashboard-muted">
														{formatPricePaisa(item.unitPrice)}
													</td>
													<td className="py-2 px-3 text-right font-mono font-semibold text-dashboard-text">
														{formatPricePaisa(item.lineTotal)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}
